// Briques communes des surfaces : chargement de données, notifications,
// modale, badges. Volontairement dans un seul fichier — ce sont de petites
// pièces liées, les éparpiller coûterait plus qu'il ne rapporte.

import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { FileUp, X } from 'lucide-react'
import { Button, ScoreRing, type ButtonSize, type ButtonVariant } from '@ai-job-fit/ui'
import { post } from '../lib/api'
import { scoreTone } from '../lib/format'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'

// --- Chargement de données ---------------------------------------------------

export interface Async<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/** Charge une ressource et la recharge sur demande. `deps` refait la requête
 *  quand l'identifiant change (passer d'une offre à une autre). */
/** Événement global : « les données ont changé côté serveur, relisez ». */
export const ACTUALISER = 'radar:actualiser'

/** Demande à tous les écrans ouverts de relire leurs données, sans écran de
 *  chargement. */
export function actualiserLesEcrans(): void {
  window.dispatchEvent(new Event(ACTUALISER))
}

export function useData<T>(load: () => Promise<T>, deps: unknown[] = []): Async<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  // `load` change à chaque rendu (fonction inline) : on le lit via une ref pour
  // que l'effet ne dépende que de `deps` et du compteur de rechargement.
  const ref = useRef(load)
  ref.current = load
  // Rechargement SILENCIEUX sur l'événement global `ACTUALISER` : quand Radar
  // finit un travail de fond (cf. bandeauTaches.tsx), les écrans se mettent à
  // jour sans repasser par « Chargement… », qui ferait clignoter la page.
  const silencieux = useRef(false)
  useEffect(() => {
    const surActualisation = () => {
      silencieux.current = true
      setTick((t) => t + 1)
    }
    window.addEventListener(ACTUALISER, surActualisation)
    return () => window.removeEventListener(ACTUALISER, surActualisation)
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!silencieux.current) setLoading(true)
    silencieux.current = false
    ref
      .current()
      .then((d) => {
        if (!cancelled) { setData(d); setError(null) }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  // `reload` relit la MÊME ressource : silencieux lui aussi. Il repassait par
  // « Chargement… », qui démonte la page le temps de la requête : suivre un
  // site faisait clignoter toute la liste, et l'aperçu d'un dossier remontait
  // sur l'onglet CV après la génération de la lettre (2026-10-05). Un
  // changement de `deps` (une autre ressource) garde l'écran de chargement.
  const reload = useCallback(() => {
    silencieux.current = true
    setTick((t) => t + 1)
  }, [])
  return { data, error, loading, reload }
}

export function Loading({ label }: { label?: string }) {
  const t = useT()
  return (
    <div className="loading">
      <span className="spinner" />
      {label ?? t('common.loading')}
    </div>
  )
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>
}

// --- Notifications -----------------------------------------------------------

/** Une action dans le message, typiquement « Annuler » : un geste qui écrit
 *  quelque chose se défait là où il s'annonce. */
export type ToastAction = { label: string; onClick: () => void }
type Toast = { id: number; message: string; kind: 'ok' | 'err'; action?: ToastAction }
const ToastCtx = createContext<(message: string, kind?: 'ok' | 'err', action?: ToastAction) => void>(() => {})
export const useToast = () => useContext(ToastCtx)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const t = useT()
  const [toasts, setToasts] = useState<Toast[]>([])
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])

  const push = useCallback(
    (message: string, kind: 'ok' | 'err' = 'ok', action?: ToastAction) => {
      const id = Date.now() + Math.random()
      setToasts((t) => [...t, { id, message, kind, action }])
      // Les SUCCÈS s'effacent seuls ; les ERREURS non. Une génération de
      // dossier prend quinze secondes : si elle échoue pendant qu'on regarde
      // ailleurs, un message de cinq secondes ne sera jamais lu, et l'action
      // aura l'air d'avoir réussi. Une erreur se ferme à la main.
      // Un message qui porte une action reste le double du temps : on doit
      // avoir le temps de lire, puis de changer d'avis.
      if (kind === 'ok') setTimeout(() => dismiss(id), action ? 10000 : 5000)
    },
    [dismiss]
  )

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {/* `aria-live` : sans lui, un lecteur d'écran n'annonce jamais le
          résultat d'une action, puisque rien ne prend le focus. `polite`
          attend une pause dans la lecture plutôt que de couper la parole. */}
      <div className="toasts" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.kind}`}>
            <span className="grow">{toast.message}</span>
            {toast.action && (
              <button className="linkbtn" onClick={() => { dismiss(toast.id); toast.action!.onClick() }}>
                {toast.action.label}
              </button>
            )}
            <button className="toast-close" onClick={() => dismiss(toast.id)} aria-label={t('common.close')}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

// --- Modale ------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Sans ça, fermer la modale rendait le focus au <body> : on repartait de
    // zéro dans l'ordre de tabulation, loin du bouton qui l'avait ouverte.
    const rendreLeFocus = document.activeElement as HTMLElement | null

    const focusables = () =>
      [...(box.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? [])]

    focusables()[0]?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      // Piège à focus : sans lui, Tab sort de la modale et parcourt la page
      // qu'elle recouvre — invisible à la souris, mais on tabule dans le vide.
      if (e.key !== 'Tab') return
      const cibles = focusables()
      if (cibles.length === 0) return
      const premier = cibles[0]
      const dernier = cibles[cibles.length - 1]
      const actif = document.activeElement
      if (e.shiftKey && (actif === premier || !box.current?.contains(actif))) {
        e.preventDefault()
        dernier.focus()
      } else if (!e.shiftKey && actif === dernier) {
        e.preventDefault()
        premier.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      rendreLeFocus?.focus?.()
    }
  }, [onClose])

  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={box}>
        <h3 id={titleId}>{title}</h3>
        {children}
        {footer && <div className="mrow">{footer}</div>}
      </div>
    </div>
  )
}

// --- Badges ------------------------------------------------------------------

/** Un score dans une liste : l'anneau partagé en petit format, le même que
 *  sur la pile des décisions et dans le widget, plutôt qu'une pastille propre
 *  à la console. Même chiffre, même dessin, partout. */
export function Score({ value, anime = false }: { value: number; anime?: boolean }) {
  return <ScoreRing score={value} tone={scoreTone(value)} size={34} ariaLabel={`${value} / 100`} anime={anime} />
}

/** La pastille teintée d'un titre de panneau : une icône dans un rond de
 *  couleur douce, pour qu'un panneau se reconnaisse avant de se lire.
 *  `h` est une teinte de 0 à 360 (voir le bloc « Teintes » d'app.css). */
export function PastilleTitre({ h, children }: { h: number; children: React.ReactNode }) {
  return <span className="ph-ic" style={{ '--h': h } as React.CSSProperties} aria-hidden="true">{children}</span>
}

export function Pill({ kind, children }: { kind: string; children: React.ReactNode }) {
  return <span className={`pill ${kind}`}>{children}</span>
}

/**
 * Le bouton partagé, branché sur les notifications de la console.
 *
 * L'état d'attente et le rattrapage d'erreur vivent maintenant dans
 * `packages/ui` (la moitié des actions appellent l'IA et prennent plusieurs
 * secondes, des deux côtés du produit). Ne reste ici que ce que le paquet
 * partagé n'a pas à connaître : COMMENT cette app signale une erreur.
 */
export function ActionButton({
  onClick,
  children,
  variant = 'secondary',
  size,
  busyLabel,
  disabled,
  title,
}: {
  onClick: () => Promise<void> | void
  children: React.ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  busyLabel?: string
  disabled?: boolean
  title?: string
}) {
  const toast = useToast()
  return (
    <Button
      variant={variant}
      size={size}
      busyLabel={busyLabel}
      disabled={disabled}
      title={title}
      onClick={() => Promise.resolve(onClick())}
      onError={(e) => toast(e.message, 'err')}
    >
      {children}
    </Button>
  )
}

/**
 * Import d'un CV PDF — le même geste que l'on parte du vivier (nouveau
 * consultant) ou de sa fiche (mise à jour). `importConsultant` fait l'upsert
 * par NOM côté serveur : pré-remplir `preset` depuis une fiche met donc le
 * profil à jour au lieu de créer un doublon.
 *
 * Vit ici plutôt que dans une page parce qu'il en sert deux : recopié, il aurait
 * divergé (c'est déjà l'histoire des composants de base de ce dépôt).
 */
/** Un fichier déposé échappe au filtre `accept` du champ : on le vérifie à la main. */
export function estUnPdf(fichier: File): boolean {
  return fichier.type === 'application/pdf' || fichier.name.toLowerCase().endsWith('.pdf')
}

export function ImportCv({
  preset,
  onClose,
  onDone,
}: {
  preset?: string
  onClose: () => void
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState(preset ?? '')
  const [survol, setSurvol] = useState(false)
  const toast = useToast()
  const mots = useMots()
  const t = useT()

  // Le clic et le dépôt (issue #229) passent par le même contrôle.
  const choisir = (f: File | undefined) => {
    if (!f) return
    if (!estUnPdf(f)) { toast(t('importCv.needFile'), 'err'); return }
    setFile(f)
  }

  return (
    <Modal
      title={preset ? t('importCv.updateTitle', { name: preset }) : mots('import.titre')}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <ActionButton
            variant="primary"
            busyLabel={t('importCv.importing')}
            onClick={async () => {
              if (!file) { toast(t('importCv.needFile'), 'err'); return }
              const form = new FormData()
              form.append('file', file)
              form.append('name', name)
              try {
                const r = await post<{ name: string }>('/api/consultants/import', form)
                toast(preset ? t('importCv.updatedToast', { name: r.name }) : mots('import.succesImporte').replace('{name}', r.name))
                // Radar catégorise et évalue ce CV aussitôt : le bandeau le montre.
                window.dispatchEvent(new Event('radar:tache-lancee'))
                onDone()
                onClose()
              } catch (e: any) { toast(e.message, 'err') }
            }}
          >
            {preset ? t('common.update') : t('common.import')}
          </ActionButton>
        </>
      }
    >
      <div className="field">
        <label className="field-label">{t('importCv.fileLabel')}</label>
        <label
          className={`bvn-depot depot-cv${survol ? ' survol' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setSurvol(true) }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => { e.preventDefault(); setSurvol(false); choisir(e.dataTransfer.files?.[0]) }}
        >
          <input type="file" accept="application/pdf" hidden onChange={(e) => { choisir(e.target.files?.[0]); e.target.value = '' }} />
          <FileUp size={22} />
          {file ? <b>{file.name}</b> : <span>{t('importCv.depot')}</span>}
        </label>
        <div className="field-help">{t('importCv.fileHelp')}</div>
      </div>
      <div className="field">
        <label className="field-label">{t('importCv.nameLabel')}</label>
        <input className="input-field" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('importCv.namePlaceholder')} />
        <div className="field-help">{mots('import.aide')}</div>
      </div>
    </Modal>
  )
}
